import { isNativeApp, isNativePushEnabled } from '../utils/platform'
import { cancelNativeIncomingCall } from './voice-call/native-call'

export { registerPushToken } from './appsync-client'

export type PushPermissionStatus = 'granted' | 'denied' | 'prompt' | 'unsupported'

/** Check native push permission (Capacitor). Web returns unsupported. */
export async function getPushPermissionStatus(): Promise<PushPermissionStatus> {
  if (!isNativeApp() || !isNativePushEnabled()) return 'unsupported'
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    const perm = await PushNotifications.checkPermissions()
    if (perm.receive === 'granted') return 'granted'
    if (perm.receive === 'denied') return 'denied'
    return 'prompt'
  } catch {
    return 'unsupported'
  }
}

/** Request native push permission and register for a token when granted. */
export async function requestPushPermission(): Promise<PushPermissionStatus> {
  if (!isNativeApp() || !isNativePushEnabled()) return 'unsupported'
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    let perm = await PushNotifications.checkPermissions()
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions()
    }
    if (perm.receive === 'granted') {
      await PushNotifications.register()
      return 'granted'
    }
    if (perm.receive === 'denied') return 'denied'
    return 'prompt'
  } catch {
    return 'unsupported'
  }
}

/**
 * Remove delivered system notifications matching an Android notification tag
 * (e.g. `call-<callId>`). Used when a call is answered/declined in-app or a
 * VOICE_CALL_DISMISS data message arrives.
 */
export async function clearDeliveredNotificationsByTag(tag: string): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    const delivered = await PushNotifications.getDeliveredNotifications()
    const matching = delivered.notifications.filter((n) => n.tag === tag)
    if (matching.length > 0) {
      await PushNotifications.removeDeliveredNotifications({ notifications: matching })
    }
  } catch {
    /* plugin unavailable — nothing to clear */
  }
}

/**
 * Convenience wrapper for voice-call notifications.
 * `answered: true` = the user answered on THIS device: the ring surfaces are
 * cleared but Android PROMOTES the ringing Telecom connection to ACTIVE
 * instead of destroying it. Destroying it left the callee with no Telecom
 * call, so the keep-alive re-reported it as OUTGOING (placeCall/DIALING on
 * the answering side — 2026-09-16 telemetry, calls 4e672972/e42ddb61).
 */
export function clearCallNotification(
  callId: string,
  options?: {
    answered?: boolean
    /** Answered call identity: Android stamps it on the Telecom call (car / BT). */
    peerId?: string
    peerName?: string
    callerName?: string
  },
): void {
  void clearDeliveredNotificationsByTag(`call-${callId}`)
  void cancelIncomingCallActionNotification(callId)
  // Dismiss the native full-screen CallStyle notification / activity too.
  if (options?.answered) {
    void cancelNativeIncomingCall(callId, 'answered', {
      peerId: options.peerId,
      peerName: options.peerName,
      callerName: options.callerName,
    })
  } else {
    void cancelNativeIncomingCall(callId, 'canceled')
  }
}

// --- Incoming-call local notification with Answer / Decline actions ---
//
// FCM tray notifications can't carry action buttons with the stock Capacitor
// push plugin. When the app process is alive (foreground/background) we get
// the INVITE via the AppSync subscription, replace the server notification
// with a local one that has Answer/Decline actions, and handle the taps in JS.
// Limitation: if the app process is killed, only the plain FCM notification
// shows (tap opens the app and the in-app call UI takes over).

const INCOMING_CALL_ACTION_TYPE = 'incoming_call'

/** Stable 31-bit notification id derived from the callId. */
function callNotificationId(callId: string): number {
  let hash = 0
  for (let i = 0; i < callId.length; i++) {
    hash = (hash * 31 + callId.charCodeAt(i)) | 0
  }
  return Math.abs(hash) || 1
}

async function getLocalNotifications() {
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  // Wrap the plugin in a plain object. Returning the Capacitor plugin proxy
  // directly from an async function makes the Promise machinery read/call
  // `.then` on the proxy, which the native bridge rejects with
  // "LocalNotifications.then() is not implemented on ios".
  return { local: LocalNotifications }
}

/**
 * Register the incoming-call action type (Answer / Decline). Android renders
 * action buttons as theme-colored text buttons — custom green/red button
 * colors require a native CallStyle notification, not supported via Capacitor.
 */
export async function registerIncomingCallActionType(): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    await local.registerActionTypes({
      types: [
        {
          id: INCOMING_CALL_ACTION_TYPE,
          actions: [
            { id: 'answer', title: 'Answer', foreground: true },
            { id: 'decline', title: 'Decline', destructive: true },
          ],
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Register the session notification action types. On iOS this registers the
 * `session_checkin` UNNotificationCategory globally, so REMOTE APNs alerts
 * whose `aps.category` matches (the auto-arrival push and the session-start
 * reminder — see infrastructure senders) show a "Record arrival" action
 * button. Tapping it opens the app (foreground: true) and Capacitor reports
 * `actionId === 'check-in'`; the push handler then records the arrival
 * immediately. Android ignores this — its action button is built natively
 * (SessionNotificationHelper.java).
 *
 * Also registers the local "Arrival time recorded" category whose UNDO action
 * mirrors the in-app snackbar after a dwell auto-record.
 */
export async function registerSessionCheckInActionType(): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const {
      SESSION_CHECK_IN_NOTIFICATION_ACTION,
      SESSION_CHECK_IN_NOTIFICATION_ACTION_TITLE,
      SESSION_CHECK_IN_NOTIFICATION_CATEGORY,
      SESSION_ARRIVAL_UNDO_NOTIFICATION_ACTION,
      SESSION_ARRIVAL_UNDO_NOTIFICATION_CATEGORY,
      SESSION_DEPARTURE_UNDO_NOTIFICATION_ACTION,
      SESSION_DEPARTURE_UNDO_NOTIFICATION_CATEGORY,
      SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION,
      SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION_TITLE,
      SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY,
    } = await import('@shared/sessions/check-in-notification')
    const { local } = await getLocalNotifications()
    await local.registerActionTypes({
      types: [
        {
          id: SESSION_CHECK_IN_NOTIFICATION_CATEGORY,
          actions: [
            {
              id: SESSION_CHECK_IN_NOTIFICATION_ACTION,
              title: SESSION_CHECK_IN_NOTIFICATION_ACTION_TITLE,
              foreground: true,
            },
          ],
        },
        {
          id: SESSION_ARRIVAL_UNDO_NOTIFICATION_CATEGORY,
          actions: [
            {
              id: SESSION_ARRIVAL_UNDO_NOTIFICATION_ACTION,
              title: 'Undo',
              foreground: true,
            },
          ],
        },
        {
          id: SESSION_DEPARTURE_UNDO_NOTIFICATION_CATEGORY,
          actions: [
            {
              id: SESSION_DEPARTURE_UNDO_NOTIFICATION_ACTION,
              title: 'Undo',
              foreground: true,
            },
          ],
        },
        {
          id: SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY,
          actions: [
            {
              id: SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION,
              title: SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION_TITLE,
              foreground: true,
            },
          ],
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

/** Deterministic 32-bit id for a session's arrival-recorded notification. */
function arrivalRecordedNotificationId(sessionUuid: string): number {
  let hash = 7
  for (let i = 0; i < sessionUuid.length; i++) {
    hash = (hash * 31 + sessionUuid.charCodeAt(i)) | 0
  }
  return (Math.abs(hash) % 2_000_000_000) + 5000
}

/**
 * Local "Arrival time recorded · UNDO" notification after a dwell
 * auto-record. The provider may have pocketed the phone during the grace —
 * the in-app snackbar alone would be missed. Tapping the body opens the app
 * (snackbar is on screen); the UNDO action rolls the record back.
 */
export async function showArrivalRecordedNotification(input: {
  sessionUuid: string
  recordedAt: number
}): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { SESSION_ARRIVAL_UNDO_NOTIFICATION_CATEGORY } = await import(
      '@shared/sessions/check-in-notification'
    )
    const { local } = await getLocalNotifications()
    let time = ''
    try {
      time = new Date(input.recordedAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      /* formatting best-effort */
    }
    await local.schedule({
      notifications: [
        {
          id: arrivalRecordedNotificationId(input.sessionUuid),
          title: 'Arrival time recorded',
          body: time
            ? `Recorded automatically at ${time}. Not at the session? Undo.`
            : 'Recorded automatically. Not at the session? Undo.',
          actionTypeId: SESSION_ARRIVAL_UNDO_NOTIFICATION_CATEGORY,
          extra: { sessionUuid: input.sessionUuid },
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

/** Dismiss the arrival-recorded notification (undo done / window closed). */
export async function clearArrivalRecordedNotification(sessionUuid: string): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    await local.cancel({
      notifications: [{ id: arrivalRecordedNotificationId(sessionUuid) }],
    })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Listen for the UNDO action on the arrival-recorded notification. Scoped to
 * the session detail screen that owns the undo window (the auto-record only
 * fires while that screen is foregrounded, so the listener is always alive
 * when the action can appear). Returns an unsubscribe.
 */
export async function addArrivalUndoActionListener(
  onUndo: (sessionUuid: string) => void,
): Promise<() => void> {
  if (!isNativeApp() || !isNativePushEnabled()) return () => {}
  try {
    const { SESSION_ARRIVAL_UNDO_NOTIFICATION_ACTION } = await import(
      '@shared/sessions/check-in-notification'
    )
    const { local } = await getLocalNotifications()
    const listener = await local.addListener('localNotificationActionPerformed', (event) => {
      if (event.actionId !== SESSION_ARRIVAL_UNDO_NOTIFICATION_ACTION) return
      const sessionUuid = (event.notification.extra as { sessionUuid?: string } | undefined)
        ?.sessionUuid
      if (sessionUuid) onUndo(sessionUuid)
    })
    return () => void listener.remove()
  } catch {
    return () => {}
  }
}

/** Deterministic 32-bit id for a session's departure-recorded notification. */
function departureRecordedNotificationId(sessionUuid: string): number {
  let hash = 11
  for (let i = 0; i < sessionUuid.length; i++) {
    hash = (hash * 31 + sessionUuid.charCodeAt(i)) | 0
  }
  return (Math.abs(hash) % 2_000_000_000) + 5000
}

function departureRecordedCopy(
  telehealthKind: 'phone' | 'video' | 'telehealth' | undefined,
  time: string,
): { title: string; body: string } {
  if (telehealthKind) {
    const noun = telehealthKind === 'phone' ? 'call' : 'session'
    return {
      title: telehealthKind === 'phone' ? 'Call end recorded' : 'Session end recorded',
      body: time
        ? `Recorded at ${time}. Didn't mean to end the ${noun}? Undo.`
        : `Didn't mean to end the ${noun}? Undo.`,
    }
  }
  return {
    title: 'Departure time recorded',
    body: time
      ? `Recorded at ${time}. Didn't mean to end the visit? Undo.`
      : "Didn't mean to end the visit? Undo.",
  }
}

/**
 * Local "Departure recorded · UNDO" notification after a departure record
 * (2026-10-07 field feedback: ending a session was too easy and irreversible).
 * The provider may pocket the phone right after the press-and-hold — the
 * in-app snackbar alone could be missed. The UNDO action rolls the record
 * back; the notification is cleared when the undo happens or the window closes.
 */
export async function showDepartureRecordedNotification(input: {
  sessionUuid: string
  recordedAt: number
  /** Phone / video / telehealth sessions: no "departure" or "visit" wording. */
  telehealthKind?: 'phone' | 'video' | 'telehealth'
}): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { SESSION_DEPARTURE_UNDO_NOTIFICATION_CATEGORY } = await import(
      '@shared/sessions/check-in-notification'
    )
    const { local } = await getLocalNotifications()
    let time = ''
    try {
      time = new Date(input.recordedAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      /* formatting best-effort */
    }
    await local.schedule({
      notifications: [
        {
          id: departureRecordedNotificationId(input.sessionUuid),
          ...departureRecordedCopy(input.telehealthKind, time),
          actionTypeId: SESSION_DEPARTURE_UNDO_NOTIFICATION_CATEGORY,
          extra: { sessionUuid: input.sessionUuid },
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

/** Dismiss the departure-recorded notification (undo done / window closed). */
export async function clearDepartureRecordedNotification(sessionUuid: string): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    await local.cancel({
      notifications: [{ id: departureRecordedNotificationId(sessionUuid) }],
    })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Listen for the UNDO action on the departure-recorded notification. Scoped to
 * the session detail screen that owns the undo window (the record is made from
 * that screen, so the listener is alive whenever the action can appear).
 */
export async function addDepartureUndoActionListener(
  onUndo: (sessionUuid: string) => void,
): Promise<() => void> {
  if (!isNativeApp() || !isNativePushEnabled()) return () => {}
  try {
    const { SESSION_DEPARTURE_UNDO_NOTIFICATION_ACTION } = await import(
      '@shared/sessions/check-in-notification'
    )
    const { local } = await getLocalNotifications()
    const listener = await local.addListener('localNotificationActionPerformed', (event) => {
      if (event.actionId !== SESSION_DEPARTURE_UNDO_NOTIFICATION_ACTION) return
      const sessionUuid = (event.notification.extra as { sessionUuid?: string } | undefined)
        ?.sessionUuid
      if (sessionUuid) onUndo(sessionUuid)
    })
    return () => void listener.remove()
  } catch {
    return () => {}
  }
}

/** Deterministic 32-bit id for a session's geofence-exit prompt notification. */
function geofenceExitNotificationId(sessionUuid: string): number {
  let hash = 13
  for (let i = 0; i < sessionUuid.length; i++) {
    hash = (hash * 31 + sessionUuid.charCodeAt(i)) | 0
  }
  return (Math.abs(hash) % 2_000_000_000) + 5000
}

/**
 * Local "Looks like you've left — record your departure?" prompt. Fired once
 * per geofence excursion by the in-session screen (the GPS stream that detects
 * the exit lives in the active-session store, so this works with whatever
 * position source feeds it). The body is deliberately generic — no member
 * name, no address (push-privacy rule). Tapping the body or the action opens
 * the session with `action=record-departure`, which records the departure at
 * the time the provider actually left.
 */
export async function showGeofenceExitNotification(input: {
  sessionUuid: string
  exitedAt: number
}): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const {
      SESSION_GEOFENCE_EXIT_NOTIFICATION_BODY,
      SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY,
      SESSION_GEOFENCE_EXIT_NOTIFICATION_TITLE,
    } = await import('@shared/sessions/check-in-notification')
    const { local } = await getLocalNotifications()
    await local.schedule({
      notifications: [
        {
          id: geofenceExitNotificationId(input.sessionUuid),
          title: SESSION_GEOFENCE_EXIT_NOTIFICATION_TITLE,
          body: SESSION_GEOFENCE_EXIT_NOTIFICATION_BODY,
          actionTypeId: SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY,
          extra: { sessionUuid: input.sessionUuid, exitedAt: input.exitedAt },
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

/** Dismiss the geofence-exit prompt (departure recorded, dismissed, or re-entered). */
export async function clearGeofenceExitNotification(sessionUuid: string): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    await local.cancel({
      notifications: [{ id: geofenceExitNotificationId(sessionUuid) }],
    })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Listen for the geofence-exit prompt being acted on (the "Record departure"
 * action OR a tap on the body — both mean "yes, record it"). Lives on the
 * shell-level active-session banner so it is alive whenever the prompt can
 * appear; the handler navigates to the session with `action=record-departure`.
 */
export async function addGeofenceExitRecordActionListener(
  onRecord: (sessionUuid: string, exitedAt: number | null) => void,
): Promise<() => void> {
  if (!isNativeApp() || !isNativePushEnabled()) return () => {}
  try {
    const { SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION, SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY } =
      await import('@shared/sessions/check-in-notification')
    const { local } = await getLocalNotifications()
    const listener = await local.addListener('localNotificationActionPerformed', (event) => {
      const isOurs =
        event.actionId === SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION ||
        (event.actionId === 'tap' &&
          event.notification.actionTypeId === SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY)
      if (!isOurs) return
      const extra = event.notification.extra as
        | { sessionUuid?: string; exitedAt?: number }
        | undefined
      if (extra?.sessionUuid) {
        onRecord(extra.sessionUuid, typeof extra.exitedAt === 'number' ? extra.exitedAt : null)
      }
    })
    return () => void listener.remove()
  } catch {
    return () => {}
  }
}

export async function showIncomingCallActionNotification(input: {
  callId: string
  callerName: string
}): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    // Replace the server-pushed notification (no actions) with ours.
    await clearDeliveredNotificationsByTag(`call-${input.callId}`)
    await local.schedule({
      notifications: [
        {
          id: callNotificationId(input.callId),
          title: 'Incoming call',
          body: `${input.callerName} is calling you`,
          channelId: 'incoming_calls',
          actionTypeId: INCOMING_CALL_ACTION_TYPE,
          ongoing: true,
          autoCancel: false,
          extra: { callId: input.callId },
        },
      ],
    })
  } catch {
    /* plugin unavailable */
  }
}

async function cancelIncomingCallActionNotification(callId: string): Promise<void> {
  if (!isNativeApp() || !isNativePushEnabled()) return
  try {
    const { local } = await getLocalNotifications()
    await local.cancel({ notifications: [{ id: callNotificationId(callId) }] })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Listen for Answer/Decline taps on the incoming-call local notification.
 * Returns an unsubscribe function.
 */
export async function addIncomingCallActionListener(handlers: {
  onAnswer: (callId: string) => void
  onDecline: (callId: string) => void
}): Promise<() => void> {
  if (!isNativeApp() || !isNativePushEnabled()) return () => {}
  try {
    const { local } = await getLocalNotifications()
    const listener = await local.addListener('localNotificationActionPerformed', (event) => {
      const callId = (event.notification.extra as { callId?: string } | undefined)?.callId
      if (!callId) return
      // 'tap' (notification body) just opens the app — the in-app incoming
      // call overlay takes over without auto-answering.
      if (event.actionId === 'answer') {
        handlers.onAnswer(callId)
      } else if (event.actionId === 'decline') {
        handlers.onDecline(callId)
      }
    })
    return () => void listener.remove()
  } catch {
    return () => {}
  }
}
